import { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Link } from 'lucide-react';
import type { AdrRelation, AdrRelationType, ADR } from '../../types';
import { useAdrs } from '../../stores';

const RELATION_TYPES: AdrRelationType[] = ['supersedes', 'superseded_by', 'amends', 'amended_by', 'relates_to'];

const RELATION_COLORS: Record<AdrRelationType, string> = {
  supersedes: '#ef4444',
  superseded_by: '#f97316',
  amends: '#3b82f6',
  amended_by: '#6366f1',
  relates_to: '#6b7280',
};

function RelationRow({
  relation,
  allAdrs,
  onRemove,
}: {
  relation: AdrRelation;
  allAdrs: ADR[];
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const targetAdr = allAdrs.find(a => a.id === relation.targetAdrId);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', backgroundColor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', marginBottom: 3 }}>
      <Link size={10} color={RELATION_COLORS[relation.type]} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 9, color: '#fff', fontWeight: 500, padding: '1px 5px', backgroundColor: RELATION_COLORS[relation.type], borderRadius: 3, flexShrink: 0 }}>
        {t(`adr.relation.${relation.type}` as any)}
      </span>
      <span style={{ fontSize: 10, color: '#1f2937', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {targetAdr?.title || relation.targetAdrId}
      </span>
      <button
        onClick={onRemove}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#9ca3af', flexShrink: 0 }}
      >
        <X size={10} />
      </button>
    </div>
  );
}

export const AdrRelationPicker = memo(function AdrRelationPicker({
  adrId,
  relations,
  onAdd,
  onRemove,
}: {
  adrId: string;
  relations: AdrRelation[];
  onAdd: (relation: AdrRelation) => void;
  onRemove: (targetAdrId: string, type: AdrRelationType) => void;
}) {
  const { t } = useTranslation();
  const allAdrs = useAdrs();
  const [addOpen, setAddOpen] = useState(false);
  const [selectedAdr, setSelectedAdr] = useState('');
  const [selectedType, setSelectedType] = useState<AdrRelationType>('relates_to');
  const [searchText, setSearchText] = useState('');

  // Filter out self and already-related ADRs for the same relation type
  const availableAdrs = allAdrs.filter(a => {
    if (a.id === adrId) return false;
    if (searchText && !a.title.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  const handleAdd = () => {
    if (!selectedAdr) return;
    onAdd({ targetAdrId: selectedAdr, type: selectedType });
    setSelectedAdr('');
    setSearchText('');
    setAddOpen(false);
  };

  return (
    <div>
      {relations.map((rel) => (
        <RelationRow
          key={`${rel.targetAdrId}-${rel.type}`}
          relation={rel}
          allAdrs={allAdrs}
          onRemove={() => onRemove(rel.targetAdrId, rel.type)}
        />
      ))}

      {addOpen ? (
        <div style={{ marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#f8fafc' }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('adr.relation.searchAdr')}
            style={{ width: '100%', fontSize: 10, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, marginBottom: 4 }}
            autoFocus
          />
          <div style={{ maxHeight: 120, overflowY: 'auto', marginBottom: 4 }}>
            {availableAdrs.length === 0 ? (
              <div style={{ fontSize: 10, color: '#9ca3af', padding: 4 }}>{t('adr.relation.noResults')}</div>
            ) : (
              availableAdrs.map(adr => (
                <button
                  key={adr.id}
                  onClick={() => setSelectedAdr(adr.id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', fontSize: 10, padding: '3px 6px',
                    border: 'none', borderRadius: 3, cursor: 'pointer',
                    backgroundColor: selectedAdr === adr.id ? '#dbeafe' : 'transparent',
                    color: '#374151',
                  }}
                >
                  {adr.title}
                </button>
              ))
            )}
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as AdrRelationType)}
              style={{ flex: 1, fontSize: 10, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#fff', color: '#1f2937' }}
            >
              {RELATION_TYPES.map(type => (
                <option key={type} value={type}>{t(`adr.relation.${type}` as any)}</option>
              ))}
            </select>
            <button
              onClick={handleAdd}
              disabled={!selectedAdr}
              style={{ fontSize: 10, padding: '2px 8px', border: 'none', borderRadius: 4, background: '#3b82f6', color: '#fff', cursor: 'pointer', opacity: !selectedAdr ? 0.4 : 1 }}
            >
              {t('common.add')}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAddOpen(true)}
          style={{ fontSize: 10, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, padding: '4px 0' }}
        >
          <Plus size={10} /> {t('adr.relation.add')}
        </button>
      )}
    </div>
  );
});
